import { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import Container from '@mui/material/Container'
import { useAuthContext } from '../../hooks/useAuthContext'
import { IMMEDIATE_ACTION, HIDDEN_ACTIVITY_TYPES } from '../../utility/constants'
import ActivityFilter from '../../components/Dashboard/ActivityFilter'
import Switch from '@mui/material/Switch'
import {
  Box,
  Divider,
  Tab,
  Tabs,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material'
import {
  ACTIVITY_TYPES,
  PROCESS_ACTIVITY_STATUSES,
  CATEGORY_CONTEXT,
} from '../../utility/constants'
import { Autocomplete, TextField, CircularProgress } from '@mui/material'
import DelegateBox from '../../components/Dashboard/DelegateBox'
import { toast } from 'react-toastify'
import { processTemplateService } from '../../services/processTemplateService'
import { useResponsiveTypography } from '../../utility/responsiveTypography'
import MyTaskTableView from '../../components/Dashboard/MyTaskTableView'
import MyTaskCardView from '../../components/Dashboard/MyTaskCardView'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined' // Data Entry Icon
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined' // Review Icon

export default function MyTasks() {
  const [tasks, setTasks] = useState([])
  const [allTask, setAllTask] = useState([])
  const [visibleData, setVisibleData] = useState([])
  const [initialLoading, setInitialLoading] = useState(true)
  const [isActiveTasks, setIsActiveTasks] = useState(() => {
    const savedState = sessionStorage.getItem('isActiveTasks')
    return savedState !== null ? JSON.parse(savedState) : true
  })
  const [isLoading, setIsLoading] = useState(false)
  const [teamDetails, setTeamDetails] = useState([])
  const [task, setTask] = useState(null)
  const [processReferenceNumber, setProcessReferenceNumber] = useState(null)
  const [isDelegateOpen, setIsDelegateOpen] = useState(false)
  const [selectedCategory, setSelectedCategory] = useState({ id: null, name: 'All' })
  const [allCategories, setAllCategories] = useState([])
  const [actionRequired, setActionRequired] = useState(IMMEDIATE_ACTION.immediate_action_required)
  const [filterCategories, setFilterCategories] = useState([])
  const [isPreferenceFetched, setPreferencesFetched] = useState(false)
  const [isCardView, setIsCardView] = useState(() => {
    const savedState = sessionStorage.getItem('isCardView')
    return savedState !== null ? JSON.parse(savedState) : true
  })
  const [activityType, setActivityType] = useState(ACTIVITY_TYPES.REVIEW)
  const [allProcessRefNums, setAllProcessRefNums] = useState([])
  const [hiddenTaskIds, setHiddenTaskIds] = useState(new Set())

  const { user } = useAuthContext()
  const { cardIcons } = useResponsiveTypography()
  const NODE_BASE_URL = process.env.REACT_APP_NODE_BASE_URL
  const [nextCursor, setNextCursor] = useState(null)
  const [hasMore, setHasMore] = useState(true) // Tracks if there's more data
  const observerRef = useRef(null)
  const abortRef = useRef(null)
  let axiosOptions = {}
  if (user) {
    axiosOptions = {
      headers: { Authorization: `Bearer ${user.token}` },
    }
  }
  const [filter, setFilter] = useState(() => {
    return {
      categoryId: JSON.parse(sessionStorage.getItem('filterCategory')) ?? null,
      processTemplateId: JSON.parse(sessionStorage.getItem('filterProcess')) ?? null,
      activityTemplateId: JSON.parse(sessionStorage.getItem('filterActivity')) ?? null,
    }
  })
  useEffect(() => {
    return () => {
      if (abortRef.current) {
        abortRef.current.abort()
      }
    }
  }, [])

  // Save state to localStorage when it changes
  async function fetchData(cursor = null, loadRefNums = false, updatedFilter = filter) {
    if (abortRef.current) {
      abortRef.current.abort()
    }
    const controller = new AbortController()
    abortRef.current = controller
    setIsLoading(true)

    const processActivityStatus = isActiveTasks
      ? PROCESS_ACTIVITY_STATUSES.ACTIVE
      : PROCESS_ACTIVITY_STATUSES.CLOSED

    //  Convert search array to a comma-separated string for API query
    const searchQueryReference =
      updatedFilter.searchByReference?.length > 0
        ? updatedFilter.searchByReference.map((ref) => ref?.label)
        : []

    const searchQuery = `&search=${searchQueryReference?.join(',')}`

    const encodedFilter = encodeURIComponent(JSON.stringify(updatedFilter))

    const apiUrl = isCardView
      ? !isActiveTasks
        ? `${NODE_BASE_URL}/data-entry/process-data/assigned-process-data/${processActivityStatus}?limit=100${
            cursor ? `&cursor=${cursor}` : ''
          }${searchQuery}&filter=${encodedFilter}`
        : `${NODE_BASE_URL}/data-entry/process-data/assigned-process-data/${processActivityStatus}`
      : `${NODE_BASE_URL}/data-entry/process-data/assigned-tabular-process-data/${processActivityStatus}`

    try {
      const resp = await axios.get(apiUrl, { ...axiosOptions, signal: controller.signal })
      if (controller.signal.aborted) return

      const result = resp.data
      const visibleActivities = Array.isArray(result.finalData)
        ? result.finalData.filter((item) => item.is_tasks_hidden === true)
        : []
      const hiddenIds = visibleActivities.map((it) => it.id)
      setHiddenTaskIds(new Set(hiddenIds))
      if (isCardView) {
        const incoming = Array.isArray(result?.data) ? result.data : []
        const filteredIncoming = incoming.filter((proc) => !hiddenIds.includes(proc.process_id))

        if (isActiveTasks) {
          setNextCursor(null)
          setHasMore(false)
        } else {
          setNextCursor(result.nextCursor ?? null)
          setHasMore(Boolean(result.nextCursor))
        }

        // visibleData (safe)
        if (Array.isArray(result?.finalData)) {
          const matchedExtractedValues = incoming
            .map((proc) => {
              const match = result.finalData.find((fd) => fd.id === proc.process_id)
              return match
                ? { process_id: proc.process_id, extracted_values: match.extracted_values }
                : null
            })
            .filter(Boolean)
          setVisibleData(matchedExtractedValues)
        } else {
          setVisibleData([])
        }

        setTasks((prev) => {
          const merged = cursor ? [...prev, ...filteredIncoming] : filteredIncoming
          return Array.from(new Map(merged.map((t) => [t.process_id, t])).values())
        })

        setAllTask((prev) => {
          const merged = cursor ? [...prev, ...filteredIncoming] : filteredIncoming
          return Array.from(new Map(merged.map((t) => [t.process_id, t])).values())
        })

        setFilterCategories(result?.allCategories || [])
        if (loadRefNums && result.allProcessRefNums) {
          setAllProcessRefNums(result.allProcessRefNums)
        }
        setNextCursor(result.nextCursor ?? null)
        setHasMore(Boolean(result.nextCursor))
      } else {
        const incoming = Array.isArray(result) ? result : []
        // exclude hidden tasks in tabular view too
        const filteredIncoming = incoming.filter((proc) => !hiddenIds.includes(proc.process_id))
        setVisibleData([])
        setTasks(filteredIncoming)
        setAllTask(filteredIncoming)
        setFilterCategories(Array.isArray(result?.allCategories) ? result.allCategories : [])
        setNextCursor(null)
        setHasMore(false)
      }
    } catch (error) {
      if (axios.isCancel?.(error) || error.name === 'CanceledError') {
        return
      }
      console.error('Fetch error:', error)
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null
      }
    }
    setInitialLoading(false)
    setIsLoading(false)
  }

  useEffect(() => {
    // setTasks([])
    setNextCursor(null)
    ;(async () => {
      await fetchData(null, true)
    })()
  }, [user, isActiveTasks, isCardView, filter])

  useEffect(() => {
    if (isActiveTasks && isCardView) return
    if (!observerRef.current || !hasMore || isLoading) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading) {
          fetchData(nextCursor, true)
        }
      },
      { threshold: 1.0 },
      {
        root: null,
        rootMargin: '200px',
        threshold: 1.0,
      },
    )

    observer.observe(observerRef.current)

    return () => {
      if (observerRef.current) {
        observer.unobserve(observerRef.current)
      }
      observer.disconnect()
    }
  }, [isActiveTasks, isCardView, hasMore, isLoading, nextCursor, tasks.length])

  useEffect(() => {
    sessionStorage.setItem('isActiveTasks', JSON.stringify(isActiveTasks))
  }, [isActiveTasks])

  useEffect(() => {
    sessionStorage.setItem('isCardView', JSON.stringify(isCardView))
  }, [isCardView])

  useEffect(() => {
    const getDataForCategory = async () => {
      try {
        const response = await processTemplateService.getProcessForCategory(user)
        const result = response.data
        const allCategory = result?.processCategories.reduce((acc, cur) => {
          return acc.find((it) => it.id == cur.process_category_id) == undefined
            ? [...acc, { id: cur.process_category_id, name: cur.name }]
            : acc
        }, [])

        setAllCategories(allCategory)
        setPreferencesFetched(true)
      } catch (error) {
        console.error('Error fetching processes:', error)
      }
    }
    getDataForCategory()
  }, [user])

  useEffect(() => {
    const saveSelectedCategory = async () => {
      if (isPreferenceFetched) {
        const categoryId = selectedCategory.id !== null ? selectedCategory.id : -1

        await processTemplateService.savedProcessCategories(
          user,
          categoryId,
          CATEGORY_CONTEXT.MY_TASKS,
        )
      }
    }
    saveSelectedCategory()
  }, [selectedCategory])

  async function deleteTask(task) {
    try {
      let processId = task.process_id
      await axios.delete(`${NODE_BASE_URL}/data-entry/process-data/${processId}`, axiosOptions)

      let updatedTasks = tasks.filter((task) => task.process_id != processId)
      let allTasks = allTask.filter((task) => task.process_id != processId)

      setTasks(updatedTasks)
      setAllTask(allTasks)
      toast.success(`${task.process_reference} has been deleted successfully.`)
    } catch (error) {
      toast.error(error.response.data.error)
    }
  }

  async function getTeamData() {
    const filteredList = (
      await axios.get(
        `${NODE_BASE_URL}/data-entry/process-data/get-all-activity-team-map`,
        axiosOptions,
      )
    ).data

    /* remove duplicate values */
    const distinctUserList = filteredList.reduce((acc, cur) => {
      const userInAcc = acc.find((it) => it.user_id === cur.user_id)
      if (userInAcc) {
        return acc.map((it) =>
          it.user_id === cur.user_id ? { ...it, team_ids: [...it.team_ids, cur.team_id] } : it,
        )
      } else {
        return [...acc, { ...cur, team_ids: [cur.team_id] }]
      }
    }, [])
    setTeamDetails(distinctUserList)
  }

  useEffect(() => {
    getTeamData()
  }, [user, tasks])

  const breakpoints = {
    default: 4,
    1100: 3,
    700: 1,
  }

  const allRefNumInUserTask = []
  allTask?.forEach((obj) => {
    allRefNumInUserTask.push(obj.process_reference)
  })

  const filterTasks = (tasks, filter) => {
    let filTasks = tasks
    if (filter?.searchByReference?.length > 0) {
      return filTasks.filter((it) =>
        filter.searchByReference.some((ref) => ref.label === it.process_reference),
      )
    }

    if (filter.activityTemplateId) {
      return filTasks.filter((it) => it.activity_template_id == filter.activityTemplateId)
    }

    if (filter.processTemplateId) {
      return filTasks.filter((it) => it.process_template_id == filter.processTemplateId)
    }

    if (filter.categoryId) {
      return filTasks.filter((it) => it.process_category_id == filter.categoryId)
    }

    return filTasks
  }

  const deduplicatedTasks =
    filter?.categoryId == null
      ? allTask?.filter(
          (task, index, self) => index === self.findIndex((t) => t.process_id === task.process_id),
        ) || []
      : allTask || []

  const uniqueTasks = (deduplicatedTasks || []).filter(
    (task) => !hiddenTaskIds?.has?.(task.process_id),
  )

  useEffect(() => {
    setSelectedCategory((prev) => ({
      ...prev,
      id: filter.categoryId,
    }))
  }, [filter.categoryId])

  const onActivityTypeChange = (_, newValue) => {
    setActivityType(newValue)
  }

  const displayedTasks = filterTasks(
    uniqueTasks
      ?.filter((task) => !HIDDEN_ACTIVITY_TYPES.includes(task.activity_type))
      .filter((task) => {
        if (!isActiveTasks) return true // ignore immediate action filter in case of closed tasks
        if (actionRequired == IMMEDIATE_ACTION.immediate_action_required) {
          return task?.is_immediate_action_required === true
        } else if (actionRequired == IMMEDIATE_ACTION.no_immediate_action_required) {
          return task?.is_immediate_action_required === false
        } else {
          return true
        }
      }),
    filter,
  )

  const immediateTasks = displayedTasks.filter((task) => task.is_immediate_action_required)
  const nonImmediateTasks = displayedTasks.filter((task) => !task.is_immediate_action_required)

  const handleFilterChange = (newFilter) => {
    if (actionRequired === IMMEDIATE_ACTION.no_immediate_action_required) {
      setActionRequired(IMMEDIATE_ACTION.all)
    }
    setFilter(newFilter)
  }

  return (
    <>
      <Container
        sx={{ flex: 1 }}
        className="main-container"
        style={{ padding: '10px 10px', margin: '-12px auto' }}
      >
        <section className="task-subheader">
          <Box
            style={{
              ...cardIcons,
              justifyContent: 'space-between',
            }}
          >
            <section className="task-tab">
              <Box
                style={{
                  display: 'flex',
                  justifyContent: 'flex-start',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  width: '100%',
                  gap: '1rem',
                }}
              >
                {tasks?.length > 0 && (
                  <>
                    <ActivityFilter
                      className="tasktab"
                      tasks={uniqueTasks}
                      categories={allCategories}
                      filter={filter}
                      applyFilter={handleFilterChange}
                      id={JSON.stringify(filter)}
                      filterCategories={filterCategories}
                      isCardView={isCardView}
                      isActiveTasks={isActiveTasks}
                    />

                    {/* Select for Filtering Tasks */}
                    <Box display="flex" gap={2} flexWrap="wrap" alignItems="center">
                      {isActiveTasks &&
                        filterTasks(tasks, filter).some(
                          (it) => !it.is_immediate_action_required,
                        ) && (
                          <FormControl size="small" sx={{ minWidth: '160px', height: '34.25px' }}>
                            <InputLabel>Immediate Action</InputLabel>
                            <Select
                              value={actionRequired}
                              onChange={(e) => setActionRequired(e.target.value)}
                              label="Immediate Action"
                              inputProps={{
                                style: {
                                  height: '1px',
                                  width: '34.25px',
                                },
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    mt: 1,
                                  },
                                },
                              }}
                              style={{ borderRadius: '50px' }}
                            >
                              {Object.entries(IMMEDIATE_ACTION).map(([key, value], index) => (
                                <MenuItem key={index} value={value}>
                                  {value}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        )}
                      {/* Task Count - now aligned on the same row */}
                      <Typography variant="body2" sx={{ fontSize: '15px', fontWeight: '30px' }}>
                        {displayedTasks?.length === 0
                          ? `No Tasks`
                          : displayedTasks?.length === 1
                            ? `1 Task`
                            : `${displayedTasks?.length} Tasks`}
                      </Typography>
                    </Box>
                  </>
                )}
              </Box>
            </section>

            <section className="task-toggle d-flex">
              {!isCardView ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Tabs
                    value={activityType}
                    onChange={onActivityTypeChange}
                    textColor="primary"
                    variant="scrollable"
                    scrollButtons="auto"
                    TabIndicatorProps={{ style: { display: 'none' } }}
                    className="custom-tabs-container"
                  >
                    <Tab
                      icon={
                        <EditOutlinedIcon
                          sx={{ fontSize: '18px', marginRight: '4px !important' }}
                        />
                      }
                      iconPosition="start"
                      label={ACTIVITY_TYPES.DATA_ENTRY}
                      value={ACTIVITY_TYPES.DATA_ENTRY}
                      className={`custom-tab ${
                        activityType === ACTIVITY_TYPES.DATA_ENTRY ? 'active-tab' : ''
                      }`}
                    />

                    <Tab
                      icon={
                        <AssignmentOutlinedIcon
                          sx={{ fontSize: '18px', marginRight: '4px !important' }}
                        />
                      }
                      iconPosition="start"
                      label={ACTIVITY_TYPES.REVIEW}
                      value={ACTIVITY_TYPES.REVIEW}
                      className={`custom-tab ${
                        activityType === ACTIVITY_TYPES.REVIEW ? 'active-tab' : ''
                      }`}
                    />
                  </Tabs>
                </Box>
              ) : null}

              <div className="task-reference-number" style={{ marginLeft: -6 }}>
                {isCardView ? (
                  <Grid item xs="auto">
                    <Box>
                      <Autocomplete
                        className="filter-select"
                        multiple
                        id="tags-standard"
                        options={allProcessRefNums}
                        sx={{
                          minWidth: 165,

                          '& .MuiChip-root': {
                            height: 22,

                            borderRadius: '12px',

                            fontSize: '12px',

                            margin: '2px',
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            color="primary"
                            label="Reference Number"
                            sx={{
                              fontWeight: '600',

                              '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
                                top: '-7px',
                              },

                              '& .MuiAutocomplete-endAdornment': {
                                top: 'calc(50% - 14px)',
                              },

                              '& .MuiInputBase-root': {
                                paddingTop: '8px',
                              },
                            }}
                          />
                        )}
                        value={filter.searchByReference}
                        onChange={async (_, value) => {
                          setFilter((prevFilter) => ({
                            ...prevFilter,

                            searchByReference: value,
                          }))

                          setTasks([])

                          setNextCursor(null)

                          await fetchData(null, false, { ...filter, searchByReference: value })
                        }}
                      />
                    </Box>
                  </Grid>
                ) : null}
              </div>

              <Box className=" toggle-groupbtn" style={{ padding: '0px 0px' }}>
                <Box className="d-flex align-item-center ">
                  <Switch
                    checked={isActiveTasks}
                    onChange={() => {
                      setInitialLoading(true)
                      setIsActiveTasks(!isActiveTasks)
                      setActionRequired(IMMEDIATE_ACTION.all)
                      setTasks(allTask)
                      setTasks([]) // optional visual clear
                      setAllTask([])
                      setNextCursor(null)
                      setHasMore(true)
                    }}
                    sx={(theme) => ({
                      '& .MuiSwitch-switchBase': {
                        padding: 1,
                        '&.Mui-checked': {
                          color: '#6C47E4',
                          '& + .MuiSwitch-track': {
                            backgroundColor: '#E1D9FB',
                          },
                        },
                      },
                      '& .MuiSwitch-thumb': {
                        backgroundColor: isActiveTasks ? '#6C47E4' : '#FFFFFF',
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: isActiveTasks ? '#E1D9FB' : '#D9D9D9',
                        opacity: 1,
                        borderRadius: 20 / 2,
                      },
                    })}
                  />
                  <Typography
                    className="fontsize12 textPrimary fontWeight700"
                    variant="body2"
                    style={{ whiteSpace: 'nowrap', color: 'text.primary' }}
                  >
                    Active Task
                  </Typography>
                </Box>

                <Box className="d-flex align-item-center" sx={{ justifyContent: 'space-between' }}>
                  <Switch
                    checked={isCardView}
                    onChange={() => {
                      setInitialLoading(true)
                      setAllTask([])
                      setTasks([])
                      setNextCursor(null)
                      setHasMore(true)
                      setIsCardView(!isCardView)
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase': {
                        padding: 1,
                        '&.Mui-checked': {
                          color: '#6C47E4',
                          '& + .MuiSwitch-track': {
                            backgroundColor: '#E1D9FB',
                          },
                        },
                      },
                      '& .MuiSwitch-thumb': {
                        backgroundColor: isCardView ? '#6C47E4' : '#FFFFFF',
                      },
                      '& .MuiSwitch-track': {
                        backgroundColor: isCardView ? '#E1D9FB' : '#D9D9D9',
                        opacity: 1,
                        borderRadius: 20 / 2,
                      },
                    }}
                  />
                  <Typography
                    className="fontsize12 textPrimary fontWeight700"
                    variant="body2"
                    style={{ whiteSpace: 'nowrap', color: 'text.primary' }}
                  >
                    Card View
                  </Typography>
                </Box>
              </Box>
            </section>
          </Box>
        </section>

        {/* Loading Indicator */}
        {initialLoading || (isLoading && !hasMore) || (isLoading && !isCardView) ? (
          <Box
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '60vh',
            }}
          >
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <Box style={{ marginTop: '0.5rem' }}>
            {displayedTasks?.length > 0 ? (
              <>
                {isCardView ? (
                  <>
                    {/* Immediate Action Required Tasks (if "All" or "Immediate Action Required" is selected) */}
                    {immediateTasks.length > 0 && (
                      <MyTaskCardView
                        tasks={immediateTasks}
                        deleteTask={deleteTask}
                        setIsDelegateOpen={setIsDelegateOpen}
                        setTask={setTask}
                        setProcessReferenceNumber={setProcessReferenceNumber}
                        teamDetails={teamDetails}
                        breakpoints={breakpoints}
                        visibleData={visibleData}
                      />
                    )}

                    {/* Divider if "All" is selected and there are non-immediate tasks */}
                    {immediateTasks.length > 0 && nonImmediateTasks.length > 0 && (
                      <Divider style={{ margin: '20px 0px 32px 0px' }}>
                        <Typography
                          variant="h12"
                          align="center"
                          gutterBottom
                          style={{ fontSize: '13px', color: 'gray' }}
                        >
                          No immediate action required
                        </Typography>
                      </Divider>
                    )}

                    {/* Non-Immediate Action Required Tasks (if "All" or "No Immediate Action Required" is selected) */}
                    {nonImmediateTasks.length > 0 && (
                      <MyTaskCardView
                        tasks={nonImmediateTasks}
                        deleteTask={deleteTask}
                        setIsDelegateOpen={setIsDelegateOpen}
                        setTask={setTask}
                        setProcessReferenceNumber={setProcessReferenceNumber}
                        teamDetails={teamDetails}
                        breakpoints={breakpoints}
                        visibleData={visibleData}
                      />
                    )}

                    {/* Infinite Scroll Loading */}
                    {isLoading && hasMore && (
                      <Box style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
                        <CircularProgress color="primary" />
                      </Box>
                    )}

                    {/* Infinite Scroll Trigger */}
                    {!(isActiveTasks && isCardView) && (
                      <div
                        ref={observerRef}
                        style={{ height: '10px', background: 'transparent' }}
                      />
                    )}
                  </>
                ) : (
                  <MyTaskTableView
                    tasks={displayedTasks}
                    activityType={activityType}
                    setActivityType={setActivityType}
                    users={teamDetails}
                  />
                )}
              </>
            ) : (
              <Typography
                variant="body1"
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '80vh',
                  width: '100%',
                }}
              >
                No {isActiveTasks ? 'active' : 'closed'} tasks found!
              </Typography>
            )}
            {!isActiveTasks && isCardView && hasMore && (
              <div ref={observerRef} style={{ height: '10px', background: 'transparent' }} />
            )}
          </Box>
        )}

        {/* Delegate Box */}
        {isDelegateOpen && (
          <DelegateBox
            isDelegateOpen={isDelegateOpen}
            setIsDelegateOpen={setIsDelegateOpen}
            teamDetails={teamDetails}
            task={task}
            tasks={tasks}
            setTasks={setTasks}
            processReferenceNumber={processReferenceNumber}
          />
        )}
      </Container>
    </>
  )
}
